import { useCallback, useState } from "react";

export type TableColumnAlignment = "left" | "center" | "right";

export type TableSettingsColumn = {
  key: string;
  label: string;
  defaultWidth: number;
  defaultRatio: string;
  defaultAlignment: TableColumnAlignment;
  minWidth?: number;
  maxWidth?: number;
};

export type SavedTableColumnSettings = {
  width: number | null;
  alignment: TableColumnAlignment;
};

export type TableSettings = Record<string, SavedTableColumnSettings>;

function isAlignment(value: unknown): value is TableColumnAlignment {
  return value === "left" || value === "center" || value === "right";
}

function readSettings(storageKey: string, columns: TableSettingsColumn[]): TableSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) ?? "{}") as Record<string, unknown>;
    return Object.fromEntries(columns.map((column) => {
      const saved = raw[column.key];
      // Earlier drag-resize versions stored just a number. Keep those widths.
      const legacyWidth = typeof saved === "number" ? saved : undefined;
      const settings = saved && typeof saved === "object" ? saved as Partial<SavedTableColumnSettings> : {};
      return [column.key, {
        width: typeof settings.width === "number" ? settings.width : legacyWidth ?? null,
        alignment: isAlignment(settings.alignment) ? settings.alignment : column.defaultAlignment,
      }];
    }));
  } catch {
    return Object.fromEntries(columns.map((column) => [column.key, {
      width: null,
      alignment: column.defaultAlignment,
    }]));
  }
}

export function useTableSettings(storageKey: string, columns: TableSettingsColumn[]) {
  const [settings, setSettings] = useState<TableSettings>(() => readSettings(storageKey, columns));

  const updateColumn = useCallback((key: string, update: Partial<SavedTableColumnSettings>) => {
    setSettings((current) => {
      const next = { ...current, [key]: { ...current[key], ...update } };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    localStorage.removeItem(storageKey);
    setSettings(readSettings(storageKey, columns));
  }, [columns, storageKey]);

  const resizeColumn = useCallback((key: string, startWidth: number, startX: number) => {
    const column = columns.find((item) => item.key === key);
    const minimum = column?.minWidth ?? 96;
    const maximum = column?.maxWidth ?? 480;
    let lastWidth = startWidth;
    const onMove = (event: PointerEvent) => {
      lastWidth = Math.min(maximum, Math.max(minimum, Math.round(startWidth + event.clientX - startX)));
      setSettings((current) => ({ ...current, [key]: { ...current[key], width: lastWidth } }));
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      setSettings((current) => {
        const next = { ...current, [key]: { ...current[key], width: lastWidth } };
        localStorage.setItem(storageKey, JSON.stringify(next));
        return next;
      });
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }, [columns, storageKey]);

  return { settings, updateColumn, reset, resizeColumn };
}
